import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  Typography,
} from "@mui/material";
import ArrowBackIos from "@mui/icons-material/ArrowBackIos";
import MonitorHeartOutlinedIcon from "@mui/icons-material/MonitorHeartOutlined";
import { DataGrid } from "@mui/x-data-grid";
import { phappsRequest } from "@/services/apiService";
import { API_PATHS } from "@/config/apiConstants";
import { PHARMACY_HALO_COLORS } from "@/config/colors";
import { getAdminApiErrorMessage } from "@/utils/adminApiErrors";
import { pharmacyHaloDataGridSx } from "@/utils/adminTableStyles";

const BLUE = PHARMACY_HALO_COLORS.BLUE;

const TIER_STYLES = {
  Poor:      { backgroundColor: "#fecaca", color: "#b91c1c" },
  Fair:      { backgroundColor: "#fef08a", color: "#a16207" },
  Good:      { backgroundColor: "#bbf7d0", color: "#166534" },
  Excellent: { backgroundColor: "#bbf7d0", color: "#166534" },
};

const BASE_TRIAL_HEALTH_COLUMNS = [
  {
    field: "ChemistName",
    headerName: "Chemist",
    flex: 1,
    minWidth: 140,
    renderCell: (p) => (
      <Typography variant="body2" noWrap sx={{ width: 1, fontWeight: 500 }}>
        {p.value || "—"}
      </Typography>
    ),
  },
  { field: "State", headerName: "State", width: 64, align: "center", headerAlign: "center" },
  { field: "Orders30d", headerName: "Orders 30d", width: 90, type: "number", align: "center", headerAlign: "center" },
  {
    field: "OrdersPerDay",
    headerName: "Ord/Day",
    width: 78,
    type: "number",
    align: "center",
    headerAlign: "center",
    valueFormatter: (v) => Number(v ?? 0).toFixed(2),
  },
  { field: "Sms30d", headerName: "SMS 30d", width: 82, type: "number", align: "center", headerAlign: "center" },
  { field: "Staff", headerName: "Staff", width: 60, type: "number", align: "center", headerAlign: "center" },
  { field: "Terminals", headerName: "PCs", width: 56, type: "number", align: "center", headerAlign: "center" },
  {
    field: "Score",
    headerName: "Score",
    width: 68,
    type: "number",
    align: "center",
    headerAlign: "center",
    valueFormatter: (v) => Number(v ?? 0).toFixed(2),
  },
  {
    field: "Tier",
    headerName: "Health",
    width: 90,
    align: "center",
    headerAlign: "center",
    renderCell: (p) => {
      const style = TIER_STYLES[p.value] || {};
      return (
        <Box sx={{ px: 1, py: 0.25, borderRadius: 1, fontSize: 11, fontWeight: 700, ...style }}>
          {p.value || "—"}
        </Box>
      );
    },
  },
];

const AdminTrialHealthPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);

  const trialHealthColumns = [
    ...BASE_TRIAL_HEALTH_COLUMNS,
    {
      field: "actions",
      headerName: "Open",
      width: 86,
      sortable: false,
      align: "center",
      headerAlign: "center",
      renderCell: (p) => (
        <Button
          size="small"
          variant="contained"
          onClick={() => p.row.ChemistUUID && navigate(`/admin-functions/chemist/${encodeURIComponent(p.row.ChemistUUID)}`)}
          disabled={!p.row.ChemistUUID}
          sx={{ textTransform: "none", fontWeight: 600, backgroundColor: BLUE, minWidth: 60 }}
        >
          Open
        </Button>
      ),
    },
  ];

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await phappsRequest(API_PATHS.ADMIN_TRIAL_HEALTH, "GET");
      setData(res);
    } catch (e) {
      setError(getAdminApiErrorMessage(e, "Failed to load trial health data"));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const trialRows = (data?.TrialHealth ?? []).map((r, i) => ({ id: r.ChemistID ?? i, ...r }));

  return (
    <div className="min-h-screen bg-white p-4 min-w-0 max-w-full">
      <Box sx={{ mb: 1.5 }}>
        <Button
          size="small"
          startIcon={<ArrowBackIos fontSize="small" />}
          onClick={() => navigate("/admin")}
          sx={{ textTransform: "none", color: "text.secondary" }}
        >
          Back
        </Button>
      </Box>

      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 3 }}>
        <MonitorHeartOutlinedIcon sx={{ color: BLUE, fontSize: 28 }} />
        <Typography variant="h5" sx={{ fontWeight: 800, color: BLUE }}>
          Trial Health
        </Typography>
      </Box>

      {loading && !data && (
        <Box className="flex items-center justify-center min-h-[320px]">
          <Typography color="textSecondary">Loading trial health…</Typography>
        </Box>
      )}

      {error && (
        <Alert severity="warning" sx={{ mb: 2 }}>{error}</Alert>
      )}

      {data && (
        <Box
          sx={{
            p: 2.5,
            borderRadius: 2,
            border: "1px solid #e5e7eb",
            backgroundColor: "#fff",
          }}
        >
          {trialRows.length > 0 ? (
            <Box sx={{ height: Math.min(600, 56 + trialRows.length * 36 + 52), width: "100%" }}>
              <DataGrid
                rows={trialRows}
                columns={trialHealthColumns}
                density="compact"
                disableColumnMenu
                disableRowSelectionOnClick
                hideFooter={trialRows.length <= 25}
                pageSizeOptions={[25, 50, 100]}
                initialState={{
                  pagination: { paginationModel: { pageSize: 25 } },
                  sorting: { sortModel: [{ field: "Score", sort: "desc" }] },
                }}
                getRowClassName={(p) => `tier-row-${p.row.Tier}`}
                sx={{
                  ...pharmacyHaloDataGridSx,
                  width: "100%",
                  "& .tier-row-Poor":      { backgroundColor: "#fef2f2" },
                  "& .tier-row-Fair":      { backgroundColor: "#fefce8" },
                  "& .tier-row-Good":      { backgroundColor: "#f0fdf4" },
                  "& .tier-row-Excellent": { backgroundColor: "#f0fdf4" },
                  "& .tier-row-Poor:hover":      { backgroundColor: "#fee2e2" },
                  "& .tier-row-Fair:hover":      { backgroundColor: "#fef9c3" },
                  "& .tier-row-Good:hover":      { backgroundColor: "#dcfce7" },
                  "& .tier-row-Excellent:hover": { backgroundColor: "#dcfce7" },
                }}
              />
            </Box>
          ) : (
            <Typography variant="body2" color="textSecondary">
              No trialing chemists found.
            </Typography>
          )}
        </Box>
      )}
    </div>
  );
};

export default AdminTrialHealthPage;
